import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "src/@/components/ui/dialog";
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Table, TableBody, TableCell, TableRow, TableHeader, TableHead } from 'src/@/components/ui/table';
import Iconify from 'src/components/Iconify';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import v2_actionCriarLista from 'src/actions/v2/contatos/v2_actionCriarLista';
import v2_actionEditarLista from 'src/actions/v2/contatos/v2_actionEditarLista';
import v2_actionListarListas from 'src/actions/v2/contatos/v2_actionListarListas';
import v2_actionDeletarLista from 'src/actions/v2/contatos/v2_actionDeletarLista';

export default function ModalGerenciarListas({ open, onClose, clienteId }) {
    const { enqueueSnackbar } = useSnackbar();
    const [listas, setListas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingLista, setEditingLista] = useState(null);
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);

    // Deletion state
    const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
    const [listaToDelete, setListaToDelete] = useState(null);

    const fetchListas = async () => {
        try {
            setLoading(true);
            const response = await v2_actionListarListas(clienteId);
            setListas(response.data.data);
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Erro ao carregar listas', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchListas();
            resetForm();
        }
    }, [open]);

    const resetForm = () => {
        setIsCreating(false);
        setEditingLista(null);
        setNome('');
        setDescricao('');
    };

    const handleSave = async () => {
        if (!nome.trim()) {
            enqueueSnackbar('Nome é obrigatório', { variant: 'warning' });
            return;
        }

        try {
            setLoadingAction(true);
            if (editingLista) {
                await v2_actionEditarLista(editingLista._id, { nome, descricao });
                enqueueSnackbar('Lista atualizada com sucesso', { variant: 'success' });
            } else {
                await v2_actionCriarLista({ nome, descricao, cliente_id: clienteId });
                enqueueSnackbar('Lista criada com sucesso', { variant: 'success' });
            }
            await fetchListas();
            resetForm();
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Erro ao salvar lista', { variant: 'error' });
        } finally {
            setLoadingAction(false);
        }
    };

    const handleEdit = (lista) => {
        setEditingLista(lista);
        setNome(lista.nome);
        setDescricao(lista.descricao || '');
        setIsCreating(true);
    };

    const handleDeleteClick = (lista) => {
        setListaToDelete(lista);
        setModalDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!listaToDelete) return;
        try {
            await v2_actionDeletarLista(listaToDelete._id);
            enqueueSnackbar('Lista excluída com sucesso', { variant: 'success' });
            setModalDeleteOpen(false);
            setListaToDelete(null);
            fetchListas();
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Erro ao excluir lista', { variant: 'error' });
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
                <DialogContent className="sm:max-w-[600px] h-[600px] flex flex-col dark:bg-[#222529] dark:border-[#383838]">
                    <DialogHeader>
                        <DialogTitle className="dark:text-[#D1D2D3]">Gerenciar Listas de Contatos</DialogTitle>
                        <DialogDescription className="dark:text-[#ABABAD]">
                            Crie e gerencie suas listas para organizar seus contatos.
                        </DialogDescription>
                    </DialogHeader>

                    {!isCreating ? (
                        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
                            <div className="flex justify-end">
                                <Button onClick={() => setIsCreating(true)} size="sm" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white">
                                    <Iconify icon="fluent:add-12-filled" className="mr-2" />
                                    Nova Lista
                                </Button>
                            </div>

                            <div className="flex-1 overflow-auto border dark:border-[#383838] rounded-md">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="dark:border-[#383838]">
                                            <TableHead className="w-[80px] dark:text-[#ABABAD]">ID</TableHead>
                                            <TableHead className="dark:text-[#ABABAD]">Nome</TableHead>
                                            <TableHead className="dark:text-[#ABABAD]">Descrição</TableHead>
                                            <TableHead className="w-[100px] dark:text-[#ABABAD]">Ações</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={3} className="text-center py-8 dark:text-[#ABABAD]">Carregando...</TableCell>
                                            </TableRow>
                                        ) : listas.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground dark:text-[#ABABAD]">Nenhuma lista encontrada.</TableCell>
                                            </TableRow>
                                        ) : (
                                            listas.map((lista) => (
                                                <TableRow key={lista._id} className="dark:border-[#383838] dark:hover:bg-[#383838]/50">
                                                    <TableCell className="dark:text-[#D1D2D3]">{lista.id_lista}</TableCell>
                                                    <TableCell className="font-medium dark:text-[#D1D2D3]">{lista.nome}</TableCell>
                                                    <TableCell className="dark:text-[#ABABAD]">{lista.descricao}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-1">
                                                            <Button variant="ghost" size="icon" onClick={() => handleEdit(lista)}>
                                                                <Iconify icon="lucide:edit-2" className="w-4 h-4 dark:text-[#D1D2D3]" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(lista)}>
                                                                <Iconify icon="lucide:trash-2" className="w-4 h-4 text-red-500" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col gap-4">
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium dark:text-[#D1D2D3]">Nome da Lista</label>
                                    <Input
                                        value={nome}
                                        onChange={(e) => setNome(e.target.value)}
                                        placeholder="Ex: Clientes VIP"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium dark:text-[#D1D2D3]">Descrição (Opcional)</label>
                                    <Input
                                        value={descricao}
                                        onChange={(e) => setDescricao(e.target.value)}
                                        placeholder="Breve descrição da lista"
                                    />
                                </div>
                            </div>
                            <DialogFooter className="mt-auto">
                                <Button variant="outline" onClick={resetForm}>Cancelar</Button>
                                <ButtonLoading loading={loadingAction} onClick={handleSave}>
                                    {editingLista ? 'Salvar Alterações' : 'Criar Lista'}
                                </ButtonLoading>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ModalConfimacao
                open={modalDeleteOpen}
                onClose={() => setModalDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
                mensagem="Tem certeza que deseja excluir esta lista? Os contatos vinculados não serão excluídos."
                titulo="Excluir Lista"
            />
        </>
    );
}
